import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Mail, Phone, MapPin, Tag, Settings2, FileDown, Banknote, Inbox, Link2, Copy, AlertTriangle, BellOff, Send } from "lucide-react";
import { toast } from "sonner";
import { formatUSPhone, telHref } from "../lib/phone";
import USPhoneInput from "../components/USPhoneInput";
import USDateInput from "../components/USDateInput";
import VendorCategoriesDialog from "../components/VendorCategoriesDialog";
import VendorSpendAnalytics from "../components/VendorSpendAnalytics";
import Export1099Dialog from "../components/Export1099Dialog";
import VendorPaymentsDialog from "../components/VendorPaymentsDialog";
import VendorInvoicesInbox from "../components/VendorInvoicesInbox";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SendVendorSignupDialog from "../components/SendVendorSignupDialog";

const EMPTY = { name: "", mailing_address: "", contact_phone: "", email: "", category: "", notes: "", tax_id: "", tax_id_type: "ein" };

/** Format raw digits as EIN (##-#######) or SSN (###-##-####). */
function formatTaxId(raw, type) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (!digits) return "";
  if (type === "ssn") {
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

export default function Vendors() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  const [export1099Open, setExport1099Open] = useState(false);
  const [sendSignupOpen, setSendSignupOpen] = useState(false);
  const [paymentsFor, setPaymentsFor] = useState(null); // vendor row or null
  const [inboxOpen, setInboxOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [stale, setStale] = useState({ count: 0, total_amount: 0, threshold_days: 5 });
  const [digestMute, setDigestMute] = useState({ muted: false, muted_until: null });

  const load = async () => {
    try {
      const { data } = await api.get("/vendors");
      setItems(data.items || []);
      setCategories(data.categories || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };

  const loadPendingCount = async () => {
    try {
      const { data } = await api.get("/vendors/invoices?status=pending");
      setPendingCount((data || []).length);
    } catch { /* non-blocking */ }
    try {
      const { data } = await api.get("/vendors/invoices/stale-pending?days=5");
      setStale({
        count: data?.count || 0,
        total_amount: data?.total_amount || 0,
        threshold_days: data?.threshold_days || 5,
      });
    } catch { /* non-blocking */ }
    try {
      const { data } = await api.get("/notifications/stale-invoice-digest/status");
      setDigestMute({ muted: !!data?.muted, muted_until: data?.muted_until || null });
    } catch { /* non-blocking */ }
  };

  const muteDigest = async () => {
    try {
      const { data } = await api.post("/notifications/stale-invoice-digest/mute");
      setDigestMute({ muted: true, muted_until: data?.muted_until || null });
      const until = data?.muted_until ? new Date(data.muted_until).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "3 days";
      toast.success(`Daily digest muted until ${until}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to mute");
    }
  };

  const unmuteDigest = async () => {
    try {
      await api.post("/notifications/stale-invoice-digest/unmute");
      setDigestMute({ muted: false, muted_until: null });
      toast.success("Daily digest resumed.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to resume");
    }
  };

  useEffect(() => {
    load();
    loadPendingCount();
  }, []);

  const sharePortalLink = async (v) => {
    try {
      const { data } = await api.post(`/vendors/${v.id}/portal/issue`);
      // Backend may return a relative path (preview env) or a full URL (prod).
      // Always normalize to an absolute URL using the current window origin.
      const raw = data.portal_url || data.portal_path || `/vendor-portal/${data.token}`;
      const url = /^https?:\/\//i.test(raw) ? raw : `${window.location.origin}${raw}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Portal link copied — paste into email to ${v.name}`);
      } catch {
        window.prompt("Vendor portal link (copy):", url);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to issue link");
    }
  };

  const emailPortalCredentials = async (v) => {
    if (!v.email) {
      toast.error(`No email on file for ${v.name} — add one first`);
      return;
    }
    if (!window.confirm(
      `Email portal access to ${v.name} at ${v.email}?\n\n` +
      `A unique magic-link URL will be generated and emailed. The link acts as their password — they should bookmark it.\n\n` +
      `If a previous link existed, it will be replaced.`
    )) return;
    try {
      toast.loading("Issuing fresh portal link…", { id: `portal-email-${v.id}` });
      const { data } = await api.post(`/vendors/${v.id}/portal/email`);
      if (data.email_sent) {
        toast.success(`Portal access emailed to ${v.email}`, { id: `portal-email-${v.id}` });
      } else {
        const url = /^https?:\/\//i.test(data.portal_url) ? data.portal_url : `${window.location.origin}${data.portal_url}`;
        toast.error("Email failed — link copied to clipboard instead", { id: `portal-email-${v.id}` });
        try { await navigator.clipboard.writeText(url); } catch { /* user can retry */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to email portal link", { id: `portal-email-${v.id}` });
    }
  };

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => {
    setEditingId(v.id);
    setForm({
      name: v.name || "",
      mailing_address: v.mailing_address || "",
      contact_phone: v.contact_phone || "",
      email: v.email || "",
      category: v.category || "",
      notes: v.notes || "",
      tax_id: (v.tax_id || "").replace(/\D/g, ""),
      tax_id_type: v.tax_id_type || "ein",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    // Persist tax_id with the formatting dashes so the 1099 export and
    // CPA-facing UIs read XX-XXXXXXX or XXX-XX-XXXX exactly as the user
    // entered it on the W-9.
    const payload = { ...form, tax_id: formatTaxId(form.tax_id, form.tax_id_type) };
    try {
      if (editingId) {
        await api.put(`/vendors/${editingId}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor added");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete vendor "${name}"? Any linked work orders will be unassigned (not deleted).`)) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const filtered = items.filter((v) => {
    if (catFilter && (v.category || "") !== catFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return (
      (v.name || "").toLowerCase().includes(q) ||
      (v.email || "").toLowerCase().includes(q) ||
      (v.contact_phone || "").toLowerCase().includes(q) ||
      (v.category || "").toLowerCase().includes(q) ||
      (v.mailing_address || "").toLowerCase().includes(q)
    );
  });

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="overline text-slate-500">Directory</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="vendors-heading">
              Vendors
            </h1>
            <p className="text-sm text-slate-500 mt-1">{items.length} total · {categories.length} categor{categories.length === 1 ? "y" : "ies"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setInboxOpen(true)}
              className="btn-outline-dark flex items-center gap-2 text-sm relative"
              data-testid="vendor-inbox-btn"
              title="Vendor invoice inbox"
            >
              <Inbox className="w-4 h-4" /> Invoices
              {pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="vendor-inbox-badge">
                  {pendingCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setExport1099Open(true)}
              className="btn-outline-dark flex items-center gap-2 text-sm"
              data-testid="export-1099-btn"
              title="Year-end 1099-NEC CSV for the IRS / your CPA"
            >
              <FileDown className="w-4 h-4" /> Export 1099
            </button>
            <button
              onClick={() => setManageCatsOpen(true)}
              className="btn-outline-dark flex items-center gap-2 text-sm"
              data-testid="manage-categories-btn"
            >
              <Settings2 className="w-4 h-4" /> Manage categories
            </button>
            <button
              onClick={() => setSendSignupOpen(true)}
              className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-sm bg-blue-600 hover:bg-blue-700 text-white border border-blue-700 transition-colors"
              data-testid="send-vendor-signup-button"
              title="Email a unique sign-up link to a vendor so they can self-onboard"
            >
              <Send className="w-4 h-4" /> Send sign-up form
            </button>
            <button data-testid="add-vendor-button" onClick={openAdd} className="btn-primary-dark flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add Vendor
            </button>
          </div>
        </div>

        <VendorCategoriesDialog
          open={manageCatsOpen}
          onClose={() => setManageCatsOpen(false)}
          onChanged={load}
        />

        <SendVendorSignupDialog
          open={sendSignupOpen}
          onOpenChange={setSendSignupOpen}
          onIssued={load}
        />


        <Export1099Dialog
          open={export1099Open}
          onClose={() => setExport1099Open(false)}
        />

        {stale.count > 0 && (
          <div
            className="mb-4 flex items-start justify-between gap-4 flex-wrap bg-amber-50 border border-amber-300 rounded-md px-4 py-3"
            data-testid="stale-pending-banner"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
              <div className="text-sm text-amber-900">
                <div className="font-semibold">
                  {stale.count} {stale.count === 1 ? "invoice has" : "invoices have"} been pending for more than {stale.threshold_days} days
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  ${stale.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in vendor work waiting on your review. Acting quickly keeps your trades happy and protects your priority for future jobs.
                </div>
                {digestMute.muted && digestMute.muted_until && (
                  <div className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1.5" data-testid="digest-muted-indicator">
                    <BellOff className="w-3 h-3" />
                    Daily digest muted until{" "}
                    {new Date(digestMute.muted_until).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    <button
                      onClick={unmuteDigest}
                      data-testid="digest-unmute-btn"
                      className="underline hover:text-amber-900 ml-1"
                    >
                      resume now
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!digestMute.muted && (
                <button
                  onClick={muteDigest}
                  data-testid="digest-mute-btn"
                  className="inline-flex items-center gap-1.5 bg-white hover:bg-amber-100 text-amber-700 border border-amber-300 text-xs font-semibold px-3 py-2 rounded-sm"
                  title="Mute the daily 8am digest email for 3 days"
                >
                  <BellOff className="w-3.5 h-3.5" /> Mute 3 days
                </button>
              )}
              <button
                onClick={() => setInboxOpen(true)}
                data-testid="stale-pending-cta"
                className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-sm"
              >
                <Inbox className="w-3.5 h-3.5" /> Review now
              </button>
            </div>
          </div>
        )}

        <div className="mb-6">
          <VendorSpendAnalytics />
        </div>

        {/* Filter row */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <input
            type="search"
            placeholder="Search vendors…"
            className="input-clean max-w-xs"
            data-testid="vendor-search-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <select
            className="input-clean"
            data-testid="vendor-category-filter"
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
          {(filter || catFilter) && (
            <button onClick={() => { setFilter(""); setCatFilter(""); }} className="text-xs text-slate-500 underline" data-testid="clear-vendor-filter">
              Clear
            </button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="vendors-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Vendor</Th>
                <Th>Category</Th>
                <Th>Contact</Th>
                <Th>Mailing address</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-16 text-slate-400">
                  {items.length === 0 ? "No vendors yet — add your first vendor." : "No vendors match this filter."}
                </td></tr>
              )}
              {filtered.map((v) => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`vendor-row-${v.id}`}>
                  <Td>
                    <div className="font-semibold text-slate-900">{v.name}</div>
                    {v.notes && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{v.notes}</div>}
                  </Td>
                  <Td>
                    {v.category ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">
                        <Tag className="w-3 h-3" /> {v.category}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </Td>
                  <Td className="text-xs font-mono space-y-0.5">
                    {v.email && <div className="flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" /> <a href={`mailto:${v.email}`} className="hover:text-slate-900 hover:underline" data-testid={`vendor-email-link-${v.id}`}>{v.email}</a></div>}
                    {v.contact_phone && (
                      <div className="flex items-center gap-1 text-slate-600">
                        <Phone className="w-3 h-3" />
                        {telHref(v.contact_phone) ? (
                          <a
                            href={telHref(v.contact_phone)}
                            className="hover:text-slate-900 hover:underline"
                            data-testid={`vendor-phone-link-${v.id}`}
                            title="Click to dial"
                          >
                            {formatUSPhone(v.contact_phone)}
                          </a>
                        ) : (
                          <span>{formatUSPhone(v.contact_phone)}</span>
                        )}
                      </div>
                    )}
                    {!v.email && !v.contact_phone && <span className="text-slate-400">—</span>}
                  </Td>
                  <Td className="text-xs text-slate-600 max-w-[240px]">
                    {v.mailing_address
                      ? <span className="flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> <span className="line-clamp-2">{v.mailing_address}</span></span>
                      : <span className="text-slate-400">—</span>}
                  </Td>
                  <Td className="text-right">
                    <button
                      onClick={() => setPaymentsFor(v)}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50 px-2.5 py-1 rounded-sm mr-2"
                      title="Record / view payments"
                      data-testid={`vendor-payments-${v.id}`}
                    >
                      <Banknote className="w-3.5 h-3.5" /> Payments
                    </button>
                    <button
                      onClick={() => sharePortalLink(v)}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 px-2.5 py-1 rounded-sm mr-2"
                      title="Copy vendor-portal magic link to clipboard"
                      data-testid={`vendor-portal-share-${v.id}`}
                    >
                      <Link2 className="w-3.5 h-3.5" /> Copy link
                    </button>
                    <button
                      onClick={() => emailPortalCredentials(v)}
                      disabled={!v.email}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-sm mr-2"
                      title={v.email ? `Email portal login URL to ${v.email}` : "Add a vendor email first to enable this"}
                      data-testid={`vendor-portal-email-${v.id}`}
                    >
                      <Send className="w-3.5 h-3.5" /> Send login
                    </button>
                    <button
                      onClick={() => openEdit(v)}
                      className="text-slate-500 hover:text-slate-900 p-1 mr-1"
                      title="Edit"
                      data-testid={`edit-vendor-${v.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(v.id, v.name)}
                      className="text-slate-500 hover:text-red-600 p-1"
                      title="Delete"
                      data-testid={`delete-vendor-${v.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }}>
                {editingId ? "Edit vendor" : "Add vendor"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <Field label="Vendor name">
                <input
                  required
                  className="input-clean w-full"
                  data-testid="vendor-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Ace Plumbing"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category">
                  <select
                    className="input-clean w-full"
                    data-testid="vendor-category-input"
                    value={form.category}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__manage__") {
                        setManageCatsOpen(true);
                        return;
                      }
                      setForm({ ...form, category: v });
                    }}
                  >
                    <option value="">— Uncategorized —</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    {/* If editing a vendor whose stored category was deleted from the
                        managed list, still show it so the label isn't silently lost. */}
                    {form.category && !categories.includes(form.category) && (
                      <option value={form.category}>{form.category} (unmanaged)</option>
                    )}
                    <option disabled>──────────</option>
                    <option value="__manage__">＋ Manage categories…</option>
                  </select>
                </Field>
                <Field label="Contact phone">
                  <USPhoneInput
                    data-testid="vendor-phone-input"
                    value={form.contact_phone}
                    onChange={(digits) => setForm({ ...form, contact_phone: digits })}
                  />
                </Field>
              </div>
              <Field label="Email">
                <input
                  type="email"
                  className="input-clean w-full"
                  data-testid="vendor-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="ops@vendor.com"
                />
              </Field>
              <Field label="Tax ID — for 1099-NEC">
                <div className="grid grid-cols-3 gap-2">
                  <select
                    className="input-clean w-full"
                    data-testid="vendor-tax-id-type-select"
                    value={form.tax_id_type || "ein"}
                    onChange={(e) => {
                      const next = e.target.value;
                      // Re-format the existing digits with the new mask
                      setForm({ ...form, tax_id_type: next, tax_id: (form.tax_id || "").replace(/\D/g, "") });
                    }}
                  >
                    <option value="ein">EIN</option>
                    <option value="ssn">SSN</option>
                  </select>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={form.tax_id_type === "ssn" ? 11 : 10}
                    className="input-clean w-full font-mono col-span-2"
                    data-testid="vendor-tax-id-input"
                    value={formatTaxId(form.tax_id, form.tax_id_type)}
                    onChange={(e) => {
                      const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 9);
                      setForm({ ...form, tax_id: digits });
                    }}
                    placeholder={form.tax_id_type === "ssn" ? "XXX-XX-XXXX" : "XX-XXXXXXX"}
                    autoComplete="off"
                  />
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  Required by the IRS if you pay this vendor ≥$600 / year. Ask the vendor for a completed W-9.
                </div>
              </Field>
              <Field label="Mailing address">
                <AddressAutocomplete
                  value={form.mailing_address}
                  onChange={(v) => setForm({ ...form, mailing_address: v })}
                  onVerified={({ formatted_address }) => setForm({ ...form, mailing_address: formatted_address })}
                  placeholder="Start typing — Google will suggest"
                  dataTestId="vendor-address-input"
                />
              </Field>
              <Field label="Notes">
                <textarea
                  rows={2}
                  className="input-clean w-full"
                  data-testid="vendor-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="optional"
                />
              </Field>
              <DialogFooter>
                <button type="button" onClick={() => setOpen(false)} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="vendor-submit-button">
                  {editingId ? "Save changes" : "Add vendor"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {paymentsFor && (
          <VendorPaymentsDialog
            vendor={paymentsFor}
            onClose={() => setPaymentsFor(null)}
            onChanged={() => {/* parent doesn't need to refresh — dialog manages its own list */}}
          />
        )}

        <VendorInvoicesInbox
          open={inboxOpen}
          onClose={() => { setInboxOpen(false); loadPendingCount(); }}
          onChanged={loadPendingCount}
        />
      </div>
    </DashboardShell>
  );
}

function Th({ children, className = "" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }
